import { useState } from "react";
import { Link } from "react-router-dom";
import { downloadCsv } from "@/utils/csv";
import { formatNumber, formatSAR } from "@/utils/format";

interface AppFeeBranch {
  id: string;
  name: string;
  orders: number;
  fee: number;
  amount: number;
}

interface AppFeesSummary {
  total: number;
  orders: number;
  avg: number;
  electronic: number;
  electronicOrders: number;
  cash: number;
  cashOrders: number;
  branches: AppFeeBranch[];
}

type AppFeesRange = "today" | "week" | "month";

interface AppFeesCardProps {
  data: AppFeesSummary;
  range: AppFeesRange;
}

const RANGE_META: Record<AppFeesRange, { label: string; file: string }> = {
  today: { label: "اليوم", file: "today" },
  week: { label: "هذا الأسبوع", file: "week" },
  month: { label: "هذا الشهر", file: "month" },
};

export default function AppFeesCard({ data, range }: AppFeesCardProps) {
  const [exported, setExported] = useState<number | null>(null);
  const maxAmount = Math.max(...data.branches.map((item) => item.amount), 1);
  const rangeMeta = RANGE_META[range];

  const stats = [
    {
      label: "إجمالي رسوم التطبيق (يتحمّلها العميل)",
      value: formatSAR(data.total),
      icon: "ri-hand-coin-line",
      tone: "bg-primary-100 text-primary-700",
      strong: true,
    },
    {
      label: "عدد طلبات التطبيق",
      value: formatNumber(data.orders),
      icon: "ri-smartphone-line",
      tone: "bg-accent-100 text-accent-800",
      strong: false,
    },
    {
      label: "الرسوم لكل طلب",
      value: formatSAR(data.avg),
      icon: "ri-scales-3-line",
      tone: "bg-secondary-200 text-secondary-800",
      strong: false,
    },
  ];

  const breakdown = [
    {
      label: "محصّلة إلكترونيًا لصالح المنصة",
      hint: "لا تُخصم من مستحقات المحل",
      count: data.electronicOrders,
      value: data.electronic,
      icon: "ri-bank-card-line",
      tone: "bg-primary-100 text-primary-700",
    },
    {
      label: "مضافة إلى رسوم التطبيق المستحقة لطلبات الكاش",
      hint: "لأن المحل حصّلها من العميل",
      count: data.cashOrders,
      value: data.cash,
      icon: "ri-hand-coin-line",
      tone: "bg-red-100 text-red-600",
    },
  ];

  const handleExport = () => {
    const headers = [
      "الفرع",
      "عدد طلبات التطبيق",
      "الرسوم لكل طلب (ر.س)",
      "إجمالي الرسوم (ر.س)",
      "نسبة الفرع من الإجمالي",
    ];
    const rows = data.branches.map((item) => [
      item.name,
      item.orders,
      item.fee.toFixed(2),
      item.amount.toFixed(2),
      data.total > 0
        ? `${((item.amount / data.total) * 100).toFixed(1)}%`
        : "0%",
    ]);

    if (rows.length > 0) {
      rows.push([
        "الإجمالي",
        data.orders,
        data.avg.toFixed(2),
        data.total.toFixed(2),
        "100%",
      ]);
    }

    downloadCsv(`app-order-fees-${rangeMeta.file}.csv`, headers, rows);
    setExported(rows.length > 0 ? rows.length - 1 : 0);
    window.setTimeout(() => setExported(null), 2500);
  };

  return (
    <section className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-800 shrink-0">
            <i className="ri-smartphone-line"></i>
          </span>
          <div>
            <h3 className="text-base font-bold text-foreground-950">
              رسوم التطبيق
            </h3>
            <p className="mt-0.5 text-xs text-foreground-500">
              رسوم يتحمّلها العميل على الطلبات المنشأة من تطبيق العميل خلال
              الفترة المحددة
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 self-start sm:self-auto">
          <button
            type="button"
            onClick={handleExport}
            disabled={data.branches.length === 0}
            className="inline-flex items-center gap-2 h-9 rounded-full bg-primary-500 px-4 text-xs font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-download-2-line"></i>
            تصدير CSV
          </button>
          {exported !== null && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700">
              <i className="ri-checkbox-circle-line"></i>
              تم تصدير {exported} فرع
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {stats.map((item) => (
          <div
            key={item.label}
            className="rounded-box border border-background-200 bg-background-50 px-4 py-3"
          >
            <div className="flex items-center gap-2 text-foreground-500">
              <span
                className={`w-7 h-7 flex items-center justify-center rounded-full text-sm ${item.tone}`}
              >
                <i className={item.icon}></i>
              </span>
              <span className="text-xs">{item.label}</span>
            </div>
            <p
              className={`mt-2 font-bold ${
                item.strong
                  ? "text-2xl text-primary-700"
                  : "text-xl text-foreground-950"
              }`}
            >
              {item.value}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {breakdown.map((item) => (
          <div
            key={item.label}
            className="rounded-box border border-background-200 bg-white px-4 py-3"
          >
            <div className="flex items-center gap-2">
              <span
                className={`w-7 h-7 flex items-center justify-center rounded-full text-sm ${item.tone}`}
              >
                <i className={item.icon}></i>
              </span>
              <span className="text-sm font-semibold text-foreground-800">
                {item.label}
              </span>
            </div>
            <p className="mt-2 text-xs text-foreground-500">
              {formatNumber(item.count)} طلب — {item.hint}
            </p>
            <p className="mt-1 text-base font-bold text-foreground-950">
              {formatSAR(item.value)}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-btn border border-accent-200 bg-accent-50 px-4 py-3 text-xs leading-6 text-foreground-600">
        رسوم التطبيق 0.80 ر.س يتحملها العميل على الطلبات المنشأة من تطبيق يور
        كاشير. تُحصّل إلكترونيًا لصالح المنصة، وفي طلبات الكاش فقط تُضاف إلى رسوم
        التطبيق المستحقة على المحل لطلبات الكاش لأنه حصّلها من العميل.
      </div>

      <div className="mt-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-foreground-500">
            توزيع الرسوم حسب الفرع
          </p>
          <span className="text-[11px] text-foreground-400">
            {rangeMeta.label}
          </span>
        </div>
        {data.branches.length === 0 ? (
          <p className="rounded-btn border border-dashed border-background-300 bg-background-50 px-4 py-3 text-xs text-foreground-500">
            لا توجد رسوم تطبيق في هذه الفترة.
          </p>
        ) : (
          <ul className="space-y-1">
            {data.branches.map((item) => (
              <li key={item.name}>
                <Link
                  to={`/branches/${item.id}`}
                  className="group block rounded-btn -mx-2 px-2 py-2 transition-colors duration-200 hover:bg-background-50 cursor-pointer"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="inline-flex items-center gap-2 text-foreground-800">
                      <i className="ri-store-3-line text-foreground-400"></i>
                      <span className="font-medium group-hover:text-primary-600 transition-colors duration-200">
                        {item.name}
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">
                        ({formatNumber(item.orders)} طلب · {formatSAR(item.fee)}{" "}
                        للطلب)
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1 font-bold text-foreground-950 whitespace-nowrap">
                      {formatSAR(item.amount)}
                      <span className="w-4 h-4 flex items-center justify-center text-foreground-300 group-hover:text-primary-500 transition-colors duration-200">
                        <i className="ri-arrow-left-s-line"></i>
                      </span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-background-100">
                    <div
                      className="h-full rounded-full bg-primary-500 transition-all duration-500"
                      style={{ width: `${(item.amount / maxAmount) * 100}%` }}
                    ></div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}